import { Injectable } from '@angular/core';
import {
	CacheObservable,
	Data$,
	RemoteData,
	appEntities,
	appEntity,
	switchTap,
} from '@lycoris-nubila/ngx-lycoris';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'lodash-es';
import { Observable, from, of } from 'rxjs';
import {
	catchError,
	map,
	startWith,
	switchMap,
	switchMapTo,
	tap,
} from 'rxjs/operators';

import { ApplicationAdapter } from 'application/application.adapter';
import { AppPushApiPort } from 'application/common/port/app-push-api.port';
import { ArticleConverterPort } from 'article/port/article-converter.port';
import { ArticleRepositoryPort } from 'article/port/article-repository.port';
import { DocumentConverterPort } from 'document/port/document-converter.port';
import { DocumentRepositoryPort } from 'document/port/document-repository.port';
import { ModuleDocumentOutput } from 'module/model/module-document.output';
import { ModuleOutput } from 'module/model/module.output';
import { IModule, Module } from 'module/module';
import { IModuleGraph } from 'module/page/graph/module-graph';
import { ModuleGraphConverterPort } from 'module/page/graph/port/module-graph-converter.port';
import { ModulePageConverterPort } from 'module/page/port/module-page-converter.port';
import { ModuleQuestionConverterPort } from 'module/page/question/port/module-question-converter.port';
import { ModuleTaskConverterPort } from 'module/page/task/port/module-task-converter.port';
import { ModuleConverterPort } from 'module/port/module-converter.port';
import { ObserveModulesByIdsQuery } from 'module/query/observe-modules-by-ids.query';

import { AppLocator } from '../../app.locator';
import { CreateModuleCmd } from './command/create-module.cmd';
import { ModuleType } from './model/module.type';
import { ModuleGraphRepositoryPort } from './page/graph/port/module-graph-repository.port';
import { ModulePageRepositoryPort } from './page/port/module-page-repository.port';
import { ModuleQuestionRepositoryPort } from './page/question/port/module-question-repository.port';
import { ModuleTaskRepositoryPort } from './page/task/port/module-task-repository.port';
import { ModuleApiPort } from './port/module-api.port';
import { ModuleApplicationPort } from './port/module-application.port';
import { ModuleRepositoryPort } from './port/module-repository.port';
import { ObserveModulesQuery } from './query/observe-modules.query';

@Injectable()
export class ModuleApplicationAdapter
	extends ApplicationAdapter
	implements ModuleApplicationPort
{
	constructor(
		translateService: TranslateService,
		private _moduleApiPort: ModuleApiPort,
		private _moduleRepositoryPort: ModuleRepositoryPort,
		private _articleRepositoryPort: ArticleRepositoryPort,
		private _documentRepositoryPort: DocumentRepositoryPort,
		private _moduleTaskRepositoryPort: ModuleTaskRepositoryPort,
		private _modulePageRepositoryPort: ModulePageRepositoryPort,
		private _moduleGraphRepositoryPort: ModuleGraphRepositoryPort,
		private _appPushApiPort: AppPushApiPort,
		private _moduleConverterPort: ModuleConverterPort,
		private _modulePageConverterPort: ModulePageConverterPort,
		private _documentConverterPort: DocumentConverterPort,
		private _moduleGraphConverterPort: ModuleGraphConverterPort,
		private _moduleQuestionConverterPort: ModuleQuestionConverterPort,
		private _moduleTaskConverterPort: ModuleTaskConverterPort,
		private _articleConverterPort: ArticleConverterPort
	) {
		super(translateService);
	}

	private get _moduleQuestionRepositoryPort(): ModuleQuestionRepositoryPort {
		return AppLocator.inject<ModuleQuestionRepositoryPort>(
			ModuleQuestionRepositoryPort
		);
	}

	public observeAttachedModulesByIds(ids: string[]): Observable<Module[]> {
		const remote$ = from(this._moduleApiPort.fetchModulesByIds(ids)).pipe(
			switchTap((modules) =>
				this._moduleRepositoryPort.saveModules(
					modules.map((module) => this._moduleConverterPort.moduleAsEntity(module))
				)
			),
			switchMapTo(this._moduleRepositoryPort.observeModulesByIds({ ids }))
		);

		return this._moduleRepositoryPort
			.observeModulesByIds({ ids })
			.pipe(
				switchMap((modules) =>
					modules.length === ids.length ? of(modules) : remote$
				)
			);
	}

	public fetchFirstCoachingPlanModule(): Observable<string | Error> {
		return this._moduleApiPort.firstCoachingPlanModule().pipe(
			tap((module) => module && this._saveModule(module)),
			map((module) => module?.id),
			catchError(() =>
				of(
					Error(
						this._buildInfrastructureErrorMessage('fetchFirstCoachingPlanModule')
					)
				)
			)
		);
	}

	public createModule(cmd: CreateModuleCmd): Promise<ModuleOutput> {
		return from(this._moduleApiPort.createModule(cmd))
			.pipe(
				switchMap((moduleId) => this._moduleApiPort.fetchModuleById(moduleId)),
				tap((module) => this._saveModule(module))
			)
			.toPromise();
	}

	public generateModuleDocumentUrl(
		file: File
	): Observable<ModuleDocumentOutput> {
		return this._moduleApiPort.generateModuleDocumentUrl(file);
	}

	public observeModuleById(id: string): Observable<RemoteData<IModule>> {
		const api$ = from(this._moduleApiPort.fetchModuleById(id)).pipe(
			switchMap((module) => this._saveModule(module)),
			map(
				(module) =>
					({
						data: module,
						fresh: true,
					} as RemoteData<ModuleOutput>)
			),
			catchError((_) =>
				of({ fresh: true, data: null } as RemoteData<ModuleOutput>)
			),
			startWith({ fresh: false, data: null } as RemoteData<ModuleOutput>)
		);

		const repo$ = this._moduleRepositoryPort.observeModuleById(id);

		return appEntity(api$, repo$);
	}

	public observeModuleByType(type: ModuleType): Observable<RemoteData<IModule>> {
		const api$ = of({ fresh: true, data: null } as RemoteData<ModuleOutput>);

		const repo$ = this._moduleRepositoryPort.observeModuleByType(type);

		return appEntity(api$, repo$);
	}

	public fetchOnBoardingModuleId(organizationId: string): Observable<string> {
		return from(
			this._moduleApiPort.fetchModules({
				moduleType: ModuleType.ON_BOARDING,
				organizationId: organizationId,
			})
		).pipe(map((modules) => first(modules)?.id));
	}

	public observeModules(
		query: ObserveModulesQuery
	): Observable<RemoteData<IModule[]>> {
		const api$ = from(this._moduleApiPort.fetchModules(query)).pipe(
			tap((modules) =>
				this._moduleRepositoryPort.saveModules(
					modules.map((module) => this._moduleConverterPort.moduleAsEntity(module))
				)
			),
			map(
				(modules) =>
					({
						data: modules,
						fresh: true,
					} as RemoteData<ModuleOutput[]>)
			),
			catchError((_) =>
				of({ fresh: true, data: [] } as RemoteData<ModuleOutput[]>)
			),
			startWith({ fresh: false, data: [] } as RemoteData<ModuleOutput[]>)
		);

		const repo$ = this._moduleRepositoryPort.observeModules(query);

		return appEntities(api$, repo$);
	}

	@CacheObservable()
	public observeUserModulesByIds(
		query: ObserveModulesByIdsQuery
	): Observable<RemoteData<IModule[]>> {
		const api$ = from(this._moduleApiPort.fetchModulesByIds(query.ids)).pipe(
			tap((modules) =>
				this._moduleRepositoryPort.saveModules(
					modules.map((module) => this._moduleConverterPort.moduleAsEntity(module))
				)
			),
			map(
				(modules) =>
					({
						data: modules,
						fresh: true,
					} as RemoteData<ModuleOutput[]>)
			),
			catchError((_) =>
				of({ fresh: true, data: [] } as RemoteData<ModuleOutput[]>)
			),
			startWith({ fresh: false, data: [] } as RemoteData<ModuleOutput[]>)
		);

		const repo$ = this._moduleRepositoryPort.observeModulesByIds(query);

		return appEntities(api$, repo$);
	}

	public observeModuleGraphsByIds(ids: string[]): Data$<IModuleGraph[]> {
		const api$ = from(this._moduleApiPort.fetchModuleGraphsByIds(ids)).pipe(
			tap((graphs) =>
				this._moduleGraphRepositoryPort.saveModuleGraphs(
					graphs.map((graph) => this._moduleGraphConverterPort.asModuleGraph(graph))
				)
			),
			map((graphs) => ({ data: graphs, fresh: true })),
			catchError((_) => of({ fresh: true, data: [] })),
			startWith({ fresh: false, data: [] })
		);

		const repo$ = this._moduleGraphRepositoryPort.observeModuleGraphsByIds(ids);

		return appEntities(api$, repo$);
	}

	public saveModules(ids: string[]): Promise<void> {
		return from(this._moduleApiPort.fetchModulesByIds(ids))
			.pipe(
				map((modules) => this._moduleConverterPort.modulesAsEntities(modules)),
				switchMap((modules) => this._moduleRepositoryPort.saveModules(modules))
			)
			.toPromise();
	}

	private _saveModule(module: ModuleOutput): Promise<ModuleOutput> {
		return Promise.all([
			this._moduleRepositoryPort.saveModules([
				this._moduleConverterPort.moduleAsEntity(module),
			]),
			this._modulePageRepositoryPort.saveModulePages(
				module.pages.map((page) => this._modulePageConverterPort.asModulePage(page))
			),
			this._articleRepositoryPort.saveArticles([
				...module.pages
					.map((page) => page.articles || [])
					.reduce((p, c) => [...p, ...c], [])
					.map((article) => this._articleConverterPort.articleAsEntity(article)),
				...this._articleConverterPort.articlesAsEntities(module.articles),
			]),
			this._documentRepositoryPort.saveDocuments(
				module.pages
					.map((page) => page.documents || [])
					.reduce((p, c) => [...p, ...c], [])
					.map((document) => this._documentConverterPort.documentAsEntity(document))
			),
			this._moduleTaskRepositoryPort.saveModuleTasks(
				module.pages
					.map((page) => page.tasks || [])
					.reduce((p, c) => [...p, ...c], [])
					.map((task) => this._moduleTaskConverterPort.asModuleTask(task))
			),
			this._moduleQuestionRepositoryPort.saveModuleQuestions(
				module.pages
					.map((page) => page.questions || [])
					.reduce((p, c) => [...p, ...c], [])
					.map((question) =>
						this._moduleQuestionConverterPort.asModuleQuestion(question)
					)
			),
			this._moduleGraphRepositoryPort.saveModuleGraphs(
				module.pages
					.map((page) => page.graphs || [])
					.reduce((p, c) => [...p, ...c], [])
					.map((graph) => this._moduleGraphConverterPort.asModuleGraph(graph))
			),
		]).then(() => module);
	}
}
